
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { chatConstants } from "../../actions/constants";
import { playDing } from "../../utils/notifyAudio";
import { fetchMyChats } from "../../actions/chatActions";
import {
    deleteMessageSocket,
    markMessagesAsReadSocket,
    receiveMessage,
    receiveMessageUpdate,
} from "../../actions/message";
import {
    joinSocketUser,
    refreshAdminSocketAuth,
    socket,
} from "../../socket/socket";
import {
    releaseChatRoomOwner,
    resyncOwnedChatRooms,
    syncChatRoomOwner,
} from "../../utils/chatRoomMembership";

const OWNER = "admin-modern-chat-global";
const getId = (value) => String(value?._id || value?.id || value || "").trim();

export default function AdminChatRealtimeBridge() {
    const dispatch = useDispatch();
    const lastNotifiedRef = useRef(new Map());
    const userId = useSelector(
        (state) => state.auth?.user?._id || state.auth?.user?.id || null
    );
    const chats = useSelector((state) => state.chat?.chats || []);
    const activeChatId = useSelector((state) => state.chat?.activeChatId || null);
    const activeChatRef = useRef(activeChatId);
    activeChatRef.current = activeChatId;

    useEffect(() => {
        if (!userId) {
            releaseChatRoomOwner(OWNER);
            return undefined;
        }

        const resync = () => {
            refreshAdminSocketAuth();
            joinSocketUser();
            resyncOwnedChatRooms();
            dispatch(fetchMyChats({ context: "all", type: "all" }));
        };

        const onChatUpsert = (payload = {}) => {
            const chat = payload?.chat || payload;
            if (!chat?._id) return;

            dispatch({
                type: chatConstants.UPSERT_CHAT,
                payload: { chat },
            });
        };

        const onChatRemoved = (payload = {}) => {
            const chatId = payload?.chatId || payload?._id || payload;
            if (!chatId) return;

            dispatch({
                type: chatConstants.REMOVE_CHAT,
                payload: { chatId },
            });
        };

        const onMessage = (message) => {
            if (!message?._id) return;
            dispatch(receiveMessage(message, userId));

            const senderId = getId(message.sender || message.senderId);
            if (!senderId || senderId === getId(userId)) return;
            const id = getId(message._id);
            const notified = lastNotifiedRef.current;
            const now = Date.now();
            if (notified.has(id)) return;
            notified.set(id, now);
            if (notified.size > 350) {
                for (const [key, time] of notified) {
                    if (now - time > 60000 || notified.size > 250) notified.delete(key);
                }
            }
            // The messenger owns one global /admin socket; do not create a second one.
            const path = typeof window === 'undefined' ? '' : window.location.pathname;
            const visibleChat = (path === `/chat/${encodeURIComponent(getId(message.chatId))}` ||
                (path === '/chat' && getId(activeChatRef.current) === getId(message.chatId))) &&
                document.visibilityState === 'visible' && document.hasFocus();
            if (visibleChat) return;
            try { playDing(); } catch (_error) { /* no audio permission */ }
            if (typeof window === 'undefined' || !('Notification' in window)) return;
            if (Notification.permission !== 'granted') return;
            try {
                const author = message.guestSender?.name ||
                    [message.sender?.firstName, message.sender?.lastName].filter(Boolean).join(' ').trim() ||
                    message.sender?.email || 'Сообщение';
                const notification = new Notification(`ALOTB · ${author}`, {
                    body: String(message.text || message.entity?.title || message.fileName || 'Новое вложение').slice(0, 180),
                    tag: `alotb-chat-${getId(message.chatId)}`,
                });
                notification.onclick = () => {
                    window.focus();
                    window.location.assign(`/chat/${encodeURIComponent(getId(message.chatId))}`);
                    notification.close();
                };
            } catch (_error) { /* operating system/browser notifications may be blocked */ }
        };

        const onMessageUpdated = (message) => {
            const updated = message?.message || message;
            if (updated?._id) dispatch(receiveMessageUpdate(updated));
        };

        const onDeleted = (payload) => {
            const messageId = payload?.messageId || payload;
            if (messageId) dispatch(deleteMessageSocket(messageId));
        };

        const onRead = ({ chatId, userId: readerId } = {}) => {
            if (!chatId || !readerId) return;
            dispatch(markMessagesAsReadSocket(chatId, readerId, userId));
        };

        resync();

        socket.on("connect", resync);
        socket.on("chat:upsert", onChatUpsert);
        socket.on("chat:updated", onChatUpsert);
        socket.on("chat:removed", onChatRemoved);
        socket.on("newMessage", onMessage);
        // То же новое сообщение, но в личную комнату пользователя: список чатов
        // обновляется, даже если подписка на комнату самого чата не удалась.
        // Повтор одного сообщения отсекается по его номеру (reducers/chat.js).
        socket.on("chat:message", onMessage);
        socket.on("messageUpdated", onMessageUpdated);
        socket.on("messageDeleted", onDeleted);
        socket.on("messagesRead", onRead);

        return () => {
            socket.off("connect", resync);
            socket.off("chat:upsert", onChatUpsert);
            socket.off("chat:updated", onChatUpsert);
            socket.off("chat:removed", onChatRemoved);
            socket.off("newMessage", onMessage);
            socket.off("chat:message", onMessage);
            socket.off("messageUpdated", onMessageUpdated);
            socket.off("messageDeleted", onDeleted);
            socket.off("messagesRead", onRead);
        };
    }, [dispatch, userId]);

    useEffect(() => {
        if (!userId) {
            releaseChatRoomOwner(OWNER);
            return undefined;
        }

        const chatIds = (Array.isArray(chats) ? chats : [])
            .map((chat) => getId(chat?._id))
            .filter(Boolean);

        syncChatRoomOwner(OWNER, chatIds);

        return undefined;
    }, [chats, userId]);

    useEffect(() => () => {
        releaseChatRoomOwner(OWNER);
    }, []);

    return null;
}
